import { useCallback, useEffect, useRef } from "react"
import { CanvasCursor, setCanvasCursor } from "../store/slices/canvasCursor.slice"
import { useAppDispatch, useAppSelector } from "./useRedux"

const useHandTool = (enable: boolean) => {
    const dispatch = useAppDispatch()
    const canvasCursor = useAppSelector((state) => state.canvasCursor.value)
    const isEditingTextMode = useAppSelector((state) => state.editor.value.isEditingTextMode)

    const ref = useRef<HTMLDivElement>(null)

    //handle to grab canvas by `space` key or switch by `h | H` key to grab canvas
    const handleOnKeyDown = useCallback(
        (e: KeyboardEvent) => {
            if (isEditingTextMode) {
                return
            } else {
                //prevent default behavior of scroll when press space
                if (e.key === " ") {
                    e.preventDefault()
                }
                //when cursor is default and keyDown is space then change cursor to grab
                if (canvasCursor !== CanvasCursor.GrabCursor && !enable && e.key === " ") {
                    e.preventDefault()
                    dispatch(setCanvasCursor(CanvasCursor.GrabCursorBySpace))
                }
                //switch cursor to grab with short key H || h
                if (!enable && (e.key === "h" || e.key === "H")) {
                    dispatch(setCanvasCursor(CanvasCursor.GrabCursor))
                }
            }
        },
        [canvasCursor, dispatch, enable, isEditingTextMode]
    )

    //handle to clear `space` key or `v | V` to return on default cursor and select item in canvas
    const handleOnKeyUp = useCallback(
        (e: KeyboardEvent) => {
            if (isEditingTextMode) {
                return
            } else {
                //prevent default behavior of scroll when press space
                if (e.key === " ") {
                    e.preventDefault()
                }
                //return when is cursor: grab and space is pressed
                if (canvasCursor === CanvasCursor.GrabCursor && e.key === " ") {
                    return
                }
                //when is enable to grab with space then return to default cursor after space keyUp
                if (enable && e.key === " ") {
                    e.preventDefault()
                    dispatch(setCanvasCursor(CanvasCursor.DefaultCursor))
                }
                //switch cursor to default with short key V || v
                if (enable && (e.key === "v" || e.key === "V")) {
                    dispatch(setCanvasCursor(CanvasCursor.DefaultCursor))
                }
            }
        },
        [canvasCursor, dispatch, enable, isEditingTextMode]
    )

    //focus whenever is clicked on div with ref
    //and set tabIndex: -1 for preventDefault behavior on press 'space'
    const handleFocusOnClick = () => {
        ref.current?.setAttribute("tabIndex", "-1")
        ref.current?.focus()
    }

    //TO DO if will be more keyShort then move to own custom Hook
    useEffect(() => {
        const element = ref.current
        element?.setAttribute("tabIndex", "-1") //set tabIndex: -1 for preventDefault behavior on press 'space'
        element?.focus() // focus on first load

        /**
         * Safety net for the space-grab.
         *
         * keydown/keyup are bound to the canvas, so they only fire while it has focus. Press
         * space, then click into the timeline: focus moves away, the keyup never reaches the
         * canvas and the grab cursor stays on forever. That also makes the canvas feel dead,
         * because <Draggable> is disabled while GrabCursorBySpace is active.
         *
         * These two listeners only ever CLEAR that state, never set it, so they cannot put the
         * cursor into a wrong mode.
         */
        const releaseSpaceGrab = () => {
            if (canvasCursor === CanvasCursor.GrabCursorBySpace) {
                dispatch(setCanvasCursor(CanvasCursor.DefaultCursor))
            }
        }
        const handleWindowKeyUp = (e: KeyboardEvent) => {
            if (e.key === " ") releaseSpaceGrab()
        }

        element?.addEventListener("keydown", handleOnKeyDown)
        element?.addEventListener("keyup", handleOnKeyUp)
        element?.addEventListener("blur", releaseSpaceGrab)
        window.addEventListener("keyup", handleWindowKeyUp)
        window.addEventListener("blur", releaseSpaceGrab)

        return () => {
            element?.removeEventListener("keydown", handleOnKeyDown)
            element?.removeEventListener("keyup", handleOnKeyUp)
            element?.removeEventListener("blur", releaseSpaceGrab)
            window.removeEventListener("keyup", handleWindowKeyUp)
            window.removeEventListener("blur", releaseSpaceGrab)
        }
    }, [handleOnKeyDown, handleOnKeyUp, canvasCursor, dispatch])

    return { ref, handleFocusOnClick }
}

export default useHandTool
